import {
  DEFAULT_ACCESS,
  MEMBER_AND_HIGHER,
  SUPERVISOR_AND_HIGHER,
} from "@/router/roles.js";
import LoginPage from "@/pages/auth/LoginPage";
import ResetPassword from "@/features/auth/ResetPassword.jsx";
import ResetPasswordConfirm from "@/features/auth/ResetPasswordConfirm.jsx";
import EmailVerificationResend from "@/features/auth/EmailVerificationResend.jsx";
import EmailVerification from "@/features/auth/EmailVerification.jsx";
import Home from "@/pages/home/Home";
import RegisterPage from "@/pages/auth/RegisterPage";
import PrivacyPolicy from "@/pages/privacy-policy/PrivacyPolicy";
import Community from "@/features/community/Community.jsx";
import DiscussionForm from "@/features/community/DiscussionForm.jsx";
import SingleDiscussion from "@/features/community/SingleDiscussion.jsx";
import CommunityTopicSearch from "@/features/community/CommunityTopicSearch.jsx";
import CommunitySearch from "@/features/community/CommunitySearch.jsx";
import SurveyViewCreate from "@/pages/survey-views/SurveyViewCreate.tsx";
import SurveyViewUpdate from "@/pages/survey-views/SurveyViewUpdate.tsx";
import SurveyView from "@/pages/survey-views/SurveyView.tsx";
import PublicFile from "@/pages/survey-views/PublicFile.tsx";
import Organization from "@/pages/organizations/Organization.tsx";
import Directory from "@/pages/directory/Directory.tsx";
import SurveyViewList from "@/pages/survey-views/SurveyViewList.js";
import DataFiles from "@/pages/files/DataFiles.js";
import User from "@/pages/users/User.js";
import UserSettings from "@/pages/settings/UserSettings.js";
import DataBlockRequests from "@/features/evidence-requests/list";
import Surveys from "@/pages/surveys/Surveys.js";
import SurveyOverview from "@/pages/surveys/SurveyOverview";
import SurveyDetails from "@/pages/surveys/SurveyDetails.js";
import SurveySection from "@/pages/surveys/SurveySection.tsx";
import EvidenceDocumentationPage from "@/pages/evidence/EvidenceDocumentation.js";
import Evidence from "@/pages/evidence/Evidence.js";
import EvidenceDetails from "@/pages/evidence/EvidenceDetails.js";
import EvidenceEdit from "@/pages/evidence/EvidenceEdit.js";
import SubmissionSuccessPage from "@/pages/surveys/SubmissionSuccessPage.js";
import GuidancePageRoute from "@/pages/surveys/GuidancePageRoute.tsx";
import ApplyToJoinPage from "@/pages/applications/ApplyToJoinPage.tsx";
import VerifyEmailPage from "@/pages/applications/VerifyEmailPage.tsx";

export const OnboardingRoutes = [
  {
    path: "/login",
    view: LoginPage,
  },
  {
    path: "/register",
    view: RegisterPage,
  },
  {
    path: "/apply",
    view: ApplyToJoinPage,
  },
  {
    path: "/applications/verify",
    view: VerifyEmailPage,
  },
  {
    path: "/reset-password",
    children: [
      { index: true, view: ResetPassword },
      { path: "confirm", view: ResetPasswordConfirm },
    ],
  },
  {
    path: "/email-verification",
    children: [
      { index: true, view: EmailVerification },
      { path: "resend", view: EmailVerificationResend },
    ],
  },
];

export const MinimalRoutes = [
  {
    path: "/public/reports",
    children: [
      { path: "files/:uuid", view: PublicFile },
      { path: "views/:surveyViewUuid", view: SurveyView },
    ],
  },
];

export const AuthenticatedRoutes = [
  {
    path: "/",
    view: Home,
    ...DEFAULT_ACCESS,
  },
  {
    path: "/privacy-policy",
    view: PrivacyPolicy,
    ...DEFAULT_ACCESS,
  },
  {
    path: "/users/:userId",
    view: User,
    ...DEFAULT_ACCESS,
  },
  {
    path: "/settings",
    view: UserSettings,
    ...DEFAULT_ACCESS,
  },
  {
    path: "/directory",
    view: Directory,
    ...DEFAULT_ACCESS,
  },
  {
    path: "/signatories/:organizationId",
    view: Organization,
    ...DEFAULT_ACCESS,
  },
  {
    path: "/files",
    view: DataFiles,
    ...DEFAULT_ACCESS,
  },
  {
    path: "/evidence",
    children: [
      { index: true, view: Evidence, ...DEFAULT_ACCESS },
      {
        path: "documentation",
        view: EvidenceDocumentationPage,
        ...DEFAULT_ACCESS,
      },
      { path: "invites", view: DataBlockRequests, ...DEFAULT_ACCESS },
      { path: ":dataBlockId", view: EvidenceDetails, ...DEFAULT_ACCESS },
      { path: ":dataBlockId/edit", view: EvidenceEdit, ...DEFAULT_ACCESS },
    ],
  },
  {
    path: "/reports",
    children: [
      { index: true, view: Surveys, ...SUPERVISOR_AND_HIGHER },
      { path: "files/:uuid", view: PublicFile, ...DEFAULT_ACCESS },
      {
        path: "views",
        children: [
          { index: true, view: SurveyViewList, ...MEMBER_AND_HIGHER },
          { path: "form", view: SurveyViewCreate, ...SUPERVISOR_AND_HIGHER },
          { path: ":surveyViewUuid", view: SurveyView, ...DEFAULT_ACCESS },
          {
            path: ":surveyViewUuid/form",
            view: SurveyViewUpdate,
            ...SUPERVISOR_AND_HIGHER,
          },
        ],
      },
      {
        path: ":surveyId",
        children: [
          { index: true, view: SurveyDetails, ...SUPERVISOR_AND_HIGHER },
          {
            path: "section/:sectionId",
            view: SurveySection,
            ...SUPERVISOR_AND_HIGHER,
          },
          {
            path: "submission-success",
            view: SubmissionSuccessPage,
            ...SUPERVISOR_AND_HIGHER,
          },
          { path: "overview", view: SurveyOverview, ...DEFAULT_ACCESS },
          { path: "guidance", view: GuidancePageRoute, ...DEFAULT_ACCESS },
        ],
      },
    ],
  },
  {
    path: "/community",
    children: [
      { index: true, view: Community, ...MEMBER_AND_HIGHER },
      { path: "search", view: CommunitySearch, ...MEMBER_AND_HIGHER },
      {
        path: "topics/:topic",
        view: CommunityTopicSearch,
        ...MEMBER_AND_HIGHER,
      },
    ],
  },
  {
    path: "/discussions",
    children: [
      { path: "form", view: DiscussionForm, ...MEMBER_AND_HIGHER },
      {
        path: "form/:discussionId",
        view: DiscussionForm,
        ...MEMBER_AND_HIGHER,
      },
      { path: ":discussionId", view: SingleDiscussion, ...MEMBER_AND_HIGHER },
    ],
  },
];

export const flattenRoutes = (routes, parentPath = "") =>
  routes.flatMap((route) => {
    const fullPath = route.index
      ? parentPath || "/"
      : `${parentPath}/${route.path}`.replace(/\/{2,}/g, "/");

    if (route.children) {
      return flattenRoutes(route.children, fullPath);
    }

    return [{ ...route, path: fullPath }];
  });

export const AuthenticatedRouteLeaves = flattenRoutes(AuthenticatedRoutes);
